import { differenceInMinutes, isSameDay } from 'date-fns';
import { type ChatMessage, type ChatRoom, isMessagePlaceholder } from 'models';
import { useMemo } from 'react';
import { useUser } from 'features/auth';
import type { MessageDirection, MessageGroup } from '../../../types';

const MESSAGE_GROUP_THRESHOLD_MINUTES = 5;

type CreateMessageGroupsOptions = {
  currentUserId: string;
  participants: ChatRoom['participants'];
};

const getMessageAuthorId = (message: ChatMessage, currentUserId: string) =>
  isMessagePlaceholder(message) ? currentUserId : message.author;

const belongsToSameGroup = (previous: ChatMessage, current: ChatMessage, currentUserId: string) => {
  const previousAuthorId = getMessageAuthorId(previous, currentUserId);
  const currentAuthorId = getMessageAuthorId(current, currentUserId);

  if (!previousAuthorId || !currentAuthorId || previousAuthorId !== currentAuthorId) {
    return false;
  }

  if (!isSameDay(previous.createdAt, current.createdAt)) {
    return false;
  }

  return differenceInMinutes(current.createdAt, previous.createdAt) <= MESSAGE_GROUP_THRESHOLD_MINUTES;
};

const startMessageGroup = (
  message: ChatMessage,
  { currentUserId, participants }: CreateMessageGroupsOptions,
): MessageGroup => {
  const authorId = getMessageAuthorId(message, currentUserId);
  const direction: MessageDirection = authorId === currentUserId ? 'outgoing' : 'incoming';

  return {
    author: authorId
      ? {
          id: authorId,
          participant: participants[authorId],
        }
      : undefined,
    direction,
    messages: [message],
  };
};

const createMessageGroups = (messages: ChatMessage[], options: CreateMessageGroupsOptions): MessageGroup[] => {
  const groups: MessageGroup[] = [];

  for (const message of messages) {
    const lastGroup = groups.at(-1);
    const previousMessage = lastGroup?.messages.at(-1);

    if (lastGroup && previousMessage && belongsToSameGroup(previousMessage, message, options.currentUserId)) {
      lastGroup.messages.push(message);
    } else {
      groups.push(startMessageGroup(message, options));
    }
  }

  return groups;
};

export const useMessageGroups = (messages: ChatMessage[], chatroom: ChatRoom) => {
  const {
    nodeUserData: { _id: currentUserId },
  } = useUser();

  return useMemo(
    () =>
      createMessageGroups(messages, {
        currentUserId,
        participants: chatroom.participants,
      }),
    [messages, chatroom.participants, currentUserId],
  );
};
